import type { StringIndexedObject } from "@kanda-libs/ks-component-ts";
import type { Company } from "@kanda-libs/ks-frontend-services";

export function parseInsuranceDate(date?: string): Date | null {
  const formatted = date?.replace(/ /g, "")?.split("/");
  if (formatted?.length !== 3) return null;
  const reordered = [formatted[1], formatted[0], formatted[2]].join("/");
  const parsed = new Date(reordered);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed;
}

export function isValidInsuranceDate(value?: string): boolean {
  return !!parseInsuranceDate(value);
}

export function isFutureInsuranceDate(value?: string): boolean {
  const date = parseInsuranceDate(value);
  if (!date) return false;
  return date.getTime() - new Date().getTime() > 0;
}

export function reformatInsuranceExpiry(expiry?: string): string | undefined {
  if (!expiry || !expiry.includes("/")) return expiry;
  return expiry.split("/").reverse().join("-");
}

function getTradeTypeName(config: StringIndexedObject): string {
  const tradeType = config.trade_type as string | undefined;
  const tradeText = (config.trade_text as string) ?? "";
  const extra = config.extra as string | undefined;
  if (tradeType !== "other_trade") return tradeText;
  if (!tradeText.toLowerCase().includes("not listed")) return tradeText;
  return extra || tradeText;
}

// The insurance FileInput can leave the value in a few shapes (an object with a
// base64 string content, an object whose content is an array of file cards, or a
// bare array of file cards). Normalise to a valid Document object ({ content,
// name, expiry? }) or undefined so we never send an array — which the backend
// schema rejects. When undefined, the backend falls back to a placeholder.
export function normalizeInsuranceDocument(
  raw: unknown,
): StringIndexedObject | undefined {
  if (raw == null) return undefined;
  let doc: unknown = raw;
  if (Array.isArray(doc)) doc = doc[0];
  if (!doc || typeof doc !== "object") return undefined;
  const record = doc as StringIndexedObject;

  let content: unknown = record.content;
  let name: unknown = record.name;
  if (Array.isArray(content)) {
    const first = content[0];
    if (first && typeof first === "object") {
      content = (first as StringIndexedObject).content;
      name = name ?? (first as StringIndexedObject).name;
    } else {
      content = first;
    }
  }
  if (typeof content !== "string" || content.length === 0) return undefined;

  const out: StringIndexedObject = { content };
  out.name = typeof name === "string" && name.length > 0 ? name : "insurance";
  if (record.expiry)
    out.expiry = reformatInsuranceExpiry(record.expiry as string);
  return out;
}

export function formatStaffOnboardBody(values: StringIndexedObject): Company {
  const owner = (values.owner as StringIndexedObject) ?? {};
  const rawCompany = (values.company as StringIndexedObject) ?? {};
  const isSole = rawCompany.company_type === "sole_trader";

  const contactName = [owner.first_name, owner.last_name]
    .filter(Boolean)
    .join(" ")
    .trim();
  const soleInfo = (rawCompany.sole_trader_info as StringIndexedObject) ?? {};
  const limitedInfo =
    (rawCompany.limited_company_info as StringIndexedObject) ?? {};
  const contactAddress = isSole
    ? soleInfo.trading_address
    : limitedInfo.company_address;
  const tradingName = isSole ? soleInfo.trading_name : limitedInfo.company_name;

  const { work_types__selected, ...companyInfo } =
    (rawCompany.company_info as StringIndexedObject) ?? {};

  const businessConfig =
    (rawCompany.business_config as StringIndexedObject) ?? {};
  if (businessConfig.trade_type) {
    companyInfo.trade_type = businessConfig.trade_type;
    companyInfo.trade_type_name = getTradeTypeName(businessConfig);
  }

  const { insurance_document: rawInsurance, ...companyInfoRest } = companyInfo;
  const insuranceDocument = normalizeInsuranceDocument(rawInsurance);
  const finalCompanyInfo = insuranceDocument
    ? { ...companyInfoRest, insurance_document: insuranceDocument }
    : companyInfoRest;

  const merged: StringIndexedObject = {
    ...rawCompany,
    company_info: finalCompanyInfo,
    emails: owner.email ? [owner.email] : rawCompany.emails,
    contact_info: {
      ...((rawCompany.contact_info as StringIndexedObject) ?? {}),
      contact_name: contactName,
      contact_email: owner.email,
      contact_phone: owner.mobile,
      contact_address: contactAddress,
      trading_name: tradingName,
    },
  };

  return cleanCompany(merged) as unknown as Company;
}

export function cleanCompany(input: StringIndexedObject): StringIndexedObject {
  const clean = (value: unknown): unknown => {
    if (Array.isArray(value)) {
      const arr = value.map(clean).filter((item) => item !== undefined);
      return arr.length ? arr : undefined;
    }
    if (value && typeof value === "object") {
      const out: StringIndexedObject = {};
      Object.entries(value as StringIndexedObject).forEach(([key, val]) => {
        const cleaned = clean(val);
        if (cleaned !== undefined) out[key] = cleaned;
      });
      return Object.keys(out).length ? out : undefined;
    }
    if (value === "" || value === null) return undefined;
    return value;
  };
  return (clean(input) as StringIndexedObject) ?? {};
}
